import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  getDemoWorkorderNumber,
  useMarketingDemoMode,
} from "../../utils/marketingDemo";

const PHOTO_FILTERS = [
  { value: "all", label: "Alles" },
  { value: "voor", label: "Voor" },
  { value: "na", label: "Na" },
  { value: "bewijs", label: "Bewijs" },
  { value: "overig", label: "Overig" },
];

const CACHE_KEY = "logboek24.monteur.photos.cache.v1";

function isSecureFileUrl(value) {
  const raw = String(value || "").trim();
  return (
    /^\/api\/files\/[^/]+\/(view|download)$/i.test(raw) ||
    /^https?:\/\/[^/]+\/api\/files\/[^/]+\/(view|download)$/i.test(raw)
  );
}

function getPhotoIdentityKey(item) {
  const werkbonId = String(item?.werkbonId || "").trim();
  const filename = String(item?.filename || "").trim();
  const rawUrl = String(item?.url || item?.fileUrl || item?.thumbUrl || "").trim();
  const normalizedUrl = rawUrl.split("?")[0].split("#")[0];
  const basename = normalizedUrl ? normalizedUrl.substring(normalizedUrl.lastIndexOf("/") + 1) : "";
  return `${werkbonId}::${filename || basename || normalizedUrl}`;
}

export default function MonteurFotosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const marketingDemoMode = useMarketingDemoMode();
  const [werkbonnen, setWerkbonnen] = useState([]);
  const [minePhotos, setMinePhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const withCurrentSearch = React.useCallback(
    (pathname) => {
      const searchPart = String(location.search || "").trim();
      return searchPart ? `${pathname}${searchPart}` : pathname;
    },
    [location.search]
  );

  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (Array.isArray(cached)) {
        setWerkbonnen(cached);
      }
    } catch {
      // ignore cache failures
    }
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");

        const [werkbonRes, photosRes] = await Promise.all([
          api.get("/werkbonnen/mine"),
          api.get("/workorders/mine/photos"),
        ]);

        const workorders = Array.isArray(werkbonRes.data) ? werkbonRes.data : [];
        const photos = Array.isArray(photosRes.data)
          ? photosRes.data
          : Array.isArray(photosRes.data?.items)
            ? photosRes.data.items
            : [];

        setWerkbonnen(workorders);
        setMinePhotos(photos);

        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(workorders));
        } catch {
          // ignore cache failures
        }
      } catch (err) {
        setError(err?.response?.data?.message || "Foto overzicht laden mislukt");
      } finally {
        setRemoteLoaded(true);
        setLoading(false);
      }
    }

    load();
  }, []);

  const photos = useMemo(() => {
    const list = [];
    const workorderMap = new Map(
      (werkbonnen || []).map((workorder) => [
        String(workorder?._id || workorder?.id || ""),
        workorder,
      ])
    );

    for (const photo of minePhotos || []) {
      const werkbonId = String(photo?.workorderId?._id || photo?.workorderId || "");
      const workorder = workorderMap.get(werkbonId);
      const werkbonNummer =
        workorder?.werkbonNummer ||
        workorder?.nummer ||
        String(photo?.workorderId?.werkbonNummer || "").trim() ||
        "Werkbon";

      const url = toAssetUrl(photo?.fileUrl || photo?.url || photo?.filename || "");
      const thumbUrl = toAssetUrl(
        photo?.thumbnailUrl || photo?.thumbnailFilename || photo?.fileUrl || photo?.url || ""
      );
      if (!url) continue;

      list.push({
        id: `mine-${String(photo?._id || `${werkbonId}-${url}`)}`,
        werkbonId,
        werkbonNummer,
        category: String(photo?.category || "overig"),
        createdAt: photo?.createdAt || null,
        filename: String(photo?.filename || "").trim(),
        url,
        thumbUrl,
      });
    }

    if (remoteLoaded && list.length === 0) {
      for (const werkbon of werkbonnen || []) {
        const werkbonId = String(werkbon?._id || werkbon?.id || "");
        const werkbonNummer = werkbon?.werkbonNummer || werkbon?.nummer || "Werkbon";

        const modern = Array.isArray(werkbon?.photos) ? werkbon.photos : [];
        for (const photo of modern) {
          const url = toAssetUrl(photo?.url || photo?.fileUrl || photo?.filename || "");
          const thumbUrl = toAssetUrl(
            photo?.thumbnailUrl ||
              photo?.thumbnailFilename ||
              photo?.thumbUrl ||
              photo?.url ||
              photo?.fileUrl ||
              ""
          );
          if (!url) continue;

          list.push({
            id: String(photo?._id || `${werkbonId}-${photo?.filename || photo?.fileUrl || "p"}`),
            werkbonId,
            werkbonNummer,
            category: String(photo?.category || "overig"),
            createdAt: photo?.createdAt || werkbon?.updatedAt || werkbon?.createdAt || null,
            filename: String(photo?.filename || "").trim(),
            url,
            thumbUrl,
          });
        }

        const legacy = Array.isArray(werkbon?.fotos) ? werkbon.fotos : [];
        for (const value of legacy) {
          const rawValue = String(value || "").trim();
          const url = toAssetUrl(rawValue);
          if (!url) continue;

          list.push({
            id: `legacy-${werkbonId}-${rawValue}`,
            werkbonId,
            werkbonNummer,
            category: "overig",
            createdAt: werkbon?.updatedAt || werkbon?.createdAt || null,
            filename: rawValue.includes("/") ? rawValue.substring(rawValue.lastIndexOf("/") + 1) : rawValue,
            url,
            thumbUrl: url,
          });
        }
      }
    }

    const uniqueByUrl = new Map();
    for (const item of list) {
      const key = getPhotoIdentityKey(item);
      const existing = uniqueByUrl.get(key);
      if (!existing) {
        uniqueByUrl.set(key, item);
        continue;
      }

      const existingSecure =
        isSecureFileUrl(existing?.url || "") || isSecureFileUrl(existing?.thumbUrl || "");
      const candidateSecure =
        isSecureFileUrl(item?.url || "") || isSecureFileUrl(item?.thumbUrl || "");

      if (!existingSecure && candidateSecure) {
        uniqueByUrl.set(key, item);
      }
    }

    return Array.from(uniqueByUrl.values()).sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  }, [werkbonnen, minePhotos, remoteLoaded]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return photos.filter((item, index) => {
      const visibleWorkorder = marketingDemoMode
        ? getDemoWorkorderNumber(index)
        : item.werkbonNummer;
      if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
      if (!needle) return true;
      const haystack = `${visibleWorkorder} ${item.category}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [photos, search, categoryFilter, marketingDemoMode]);

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Foto's</h2>
        <p className="mt-1 text-sm text-gray-600">
          {marketingDemoMode
            ? "Recente resultaatfoto's in marketing-demo mode, zonder herleidbare metadata."
            : "Recente werkbonfoto's van jouw werkbonnen, direct terug te leiden naar de bron."}
        </p>
      </section>

      <section className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Zoek op werkbonnummer of categorie"
            className="w-full max-w-sm rounded border p-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {PHOTO_FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setCategoryFilter(option.value)}
                className={`rounded-full border px-3 py-2 text-xs font-medium ${
                  categoryFilter === option.value
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-gray-300 bg-white text-gray-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500">{filtered.length} foto's</span>
        </div>

        {loading && <p className="text-sm text-gray-500">Laden...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && !error && filtered.length === 0 && (
          <p className="text-sm text-gray-500">Geen foto's gevonden.</p>
        )}

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {filtered.map((photo, index) => {
            const visibleWorkorder = marketingDemoMode
              ? getDemoWorkorderNumber(index)
              : photo.werkbonNummer;
            return (
            <div key={photo.id} className="rounded border p-2">
              <MonteurPhotoThumb photo={photo} />
              <p className="mt-2 truncate text-xs font-medium text-gray-800">{visibleWorkorder}</p>
              <p className="text-[11px] text-gray-500">
                {formatPhotoCategory(photo.category)} | {formatShortDate(photo.createdAt)}
              </p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[11px] text-gray-500">{formatDateTime(photo.createdAt)}</span>
                <button
                  type="button"
                  onClick={() =>
                    navigate(withCurrentSearch(`/monteur/werkbonnen/${photo.werkbonId}`))
                  }
                  className="rounded border px-2 py-1 text-[11px] text-gray-700 hover:bg-gray-50"
                  disabled={!photo.werkbonId}
                >
                  Open werkbon
                </button>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => openPhotoInNewTab(photo.url)}
                  className="flex-1 rounded border px-2 py-1 text-center text-[11px] text-gray-700 hover:bg-gray-50"
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => downloadPhotoFile(photo.url, `${visibleWorkorder}-${photo.category}.jpg`)}
                  className="flex-1 rounded border px-2 py-1 text-center text-[11px] text-gray-700 hover:bg-gray-50"
                >
                  Download
                </button>
              </div>
            </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function MonteurPhotoThumb({ photo }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadingSecurePreview, setLoadingSecurePreview] = useState(false);
  const secureCandidate = photo?.thumbUrl || photo?.url || "";
  const usesSecureUrl = isSecureFileUrl(secureCandidate);

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    async function loadSecurePreview() {
      if (!usesSecureUrl) {
        setBlobUrl("");
        setLoadFailed(false);
        setLoadingSecurePreview(false);
        return;
      }

      try {
        setLoadFailed(false);
        setLoadingSecurePreview(true);
        const res = await api.get(secureCandidate, { responseType: "blob" });
        objectUrl = URL.createObjectURL(res.data);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setBlobUrl(objectUrl);
      } catch {
        if (active) {
          setBlobUrl("");
          setLoadFailed(true);
        }
      } finally {
        if (active) {
          setLoadingSecurePreview(false);
        }
      }
    }

    loadSecurePreview();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [secureCandidate, usesSecureUrl]);

  if (loadFailed && usesSecureUrl) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded bg-slate-100 px-2 text-center text-[11px] text-slate-500">
        Foto kon niet geladen worden
      </div>
    );
  }

  if (usesSecureUrl && (loadingSecurePreview || !blobUrl)) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded bg-slate-100 px-2 text-center text-[11px] text-slate-500">
        Laden...
      </div>
    );
  }

  const src = blobUrl || photo?.thumbUrl || photo?.url || "";
  if (!src) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded bg-slate-100 px-2 text-center text-[11px] text-slate-500">
        Geen foto
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={photo?.category || "Foto"}
      onError={(event) => {
        if (usesSecureUrl) {
          setLoadFailed(true);
          return;
        }
        const target = event.currentTarget;
        if (photo?.url && target.src !== photo.url) {
          target.src = photo.url;
        }
      }}
      className="aspect-square w-full rounded object-cover"
    />
  );
}

async function fetchPhotoBlob(url) {
  const value = String(url || "").trim();
  if (!value) throw new Error("Foto URL ontbreekt");
  if (isSecureFileUrl(value)) {
    const res = await api.get(value, { responseType: "blob" });
    return res.data;
  }
  const res = await fetch(value);
  if (!res.ok) throw new Error("Foto ophalen mislukt");
  return res.blob();
}

async function openPhotoInNewTab(url) {
  try {
    const blob = await fetchPhotoBlob(url);
    const objectUrl = window.URL.createObjectURL(blob);
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    console.error("Foto openen mislukt", error);
    window.alert("Foto openen mislukt.");
  }
}

async function downloadPhotoFile(url, filename) {
  try {
    const blob = await fetchPhotoBlob(url);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = String(filename || "foto.jpg").trim() || "foto.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    console.error("Foto downloaden mislukt", error);
    window.alert("Foto downloaden mislukt.");
  }
}

function toAssetUrl(raw) {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;

  const base = String(api?.defaults?.baseURL || "").replace(/\/api\/?$/, "");
  if (!base) {
    if (value.startsWith("/uploads/")) return value;
    if (value.includes("/")) return `/${value.replace(/^\/+/, "")}`;
    return `/uploads/werkbonnen/${encodeURIComponent(value)}`;
  }

  if (value.startsWith("/uploads/")) return `${base}${value}`;
  if (value.includes("/")) return `${base}/${value.replace(/^\/+/, "")}`;
  return `${base}/uploads/werkbonnen/${encodeURIComponent(value)}`;
}

function formatPhotoCategory(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "voor") return "Voor";
  if (key === "na") return "Na";
  if (key === "bewijs") return "Bewijs";
  return "Overig";
}

function formatShortDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit" });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
